import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import InputBase from '@mui/material/InputBase';
import { alpha, useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { InfoTooltip } from './chatMessageStyles';
import { GroupNameTaken } from './graphGroups';

/** The pill every chip and the inline name field share. */
export const pillSx = (theme, { active = false, error = false } = {}) => ({
  display: 'inline-flex', alignItems: 'center', gap: 0.75, flexShrink: 0,
  height: 30, px: 1.5, borderRadius: '15px', border: 1,
  borderColor: error ? 'error.main' : active ? alpha(theme.palette.primary.main, 0.7) : 'divider',
  bgcolor: active ? alpha(theme.palette.primary.main, 0.14) : 'action.hover',
  color: active ? 'primary.main' : 'text.secondary',
  fontSize: 13, fontWeight: active ? 600 : 500, whiteSpace: 'nowrap',
  transition: 'background-color 150ms, border-color 150ms, color 150ms',
});

/**
 * A group name typed in place: Enter saves, Escape cancels, leaving the field
 * saves what is typed (or cancels when it is empty). A taken name keeps the
 * field open with a hint.
 */
export function GroupNameInput({ initial = '', onSubmit, onCancel, sx }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [value, setValue] = useState(initial);
  const [error, setError] = useState('');
  // 'idle' | 'busy' | 'done' — once saved, the blur of the closing field must
  // not save a second time.
  const state = useRef('idle');
  const placeholder = t('graphs_group_name', { defaultValue: 'Name der Gruppe' });

  const submit = async () => {
    if (state.current !== 'idle') return;
    const name = value.trim();
    if (!name || name === initial.trim()) {
      state.current = 'done';
      onCancel();
      return;
    }
    state.current = 'busy';
    try {
      await onSubmit(name);
      state.current = 'done';
    } catch (e) {
      state.current = 'idle';
      setError(e instanceof GroupNameTaken
        ? t('graphs_group_exists', { defaultValue: 'Diese Gruppe gibt es schon.' })
        : t('graphs_group_error', { defaultValue: 'Das hat nicht geklappt. Bitte nochmal versuchen.' }));
    }
  };
  const cancel = () => {
    if (state.current === 'done') return;
    state.current = 'done';
    onCancel();
  };

  return (
    <InfoTooltip title={error} open={!!error}>
      <Box sx={{ ...pillSx(theme, { active: true, error: !!error }), px: 1.25, ...sx }}>
        <InputBase
          autoFocus
          value={value}
          placeholder={placeholder}
          onChange={(e) => { setValue(e.target.value); setError(''); }}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') { e.preventDefault(); submit(); }
            if (e.key === 'Escape') { e.preventDefault(); cancel(); }
          }}
          onBlur={() => { if (error) cancel(); else submit(); }}
          inputProps={{ maxLength: 80, 'aria-label': placeholder }}
          sx={{
            fontSize: 13, fontWeight: 600, color: 'text.primary',
            width: `${Math.min(Math.max(value.length, placeholder.length) + 2, 32)}ch`,
            '& input': { p: 0 },
          }}
        />
      </Box>
    </InfoTooltip>
  );
}

function Chip({ label, count, active, onClick }) {
  const theme = useTheme();
  const ref = useRef(null);
  useEffect(() => {
    if (active) ref.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }, [active]);
  const chip = (
    <ButtonBase
      ref={ref}
      onClick={onClick}
      aria-pressed={active}
      aria-label={`${label} (${count})`}
      sx={{ ...pillSx(theme, { active }), '&:hover': { borderColor: 'primary.main', color: 'primary.main' } }}
    >
      {/* a very long name is cut, the tooltip shows it whole */}
      <Box component="span" sx={{ maxWidth: LABEL_MAX, overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</Box>
      <Box component="span" sx={{ fontSize: 11, fontWeight: 600, opacity: active ? 0.85 : 0.6 }}>{count}</Box>
    </ButtonBase>
  );
  return label.length > 26 ? <InfoTooltip title={label}>{chip}</InfoTooltip> : chip;
}

const LABEL_MAX = 200;

/** The chip row's scrollbar: 4px, quiet, brighter while the row is hovered.
 *  Never `scrollbar-width` in Chrome — since Chrome 121 it then ignores the
 *  ::-webkit-scrollbar rules and draws the wide white default bar; the
 *  standard properties only for browsers without ::-webkit-scrollbar
 *  (Firefox). */
const thinScrollbarSx = (theme) => {
  const thumb = alpha(theme.palette.text.primary, 0.16);
  return {
    '&::-webkit-scrollbar': { height: 4 },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
    '&::-webkit-scrollbar-thumb': { background: thumb, borderRadius: 2 },
    '&:hover::-webkit-scrollbar-thumb': { background: alpha(theme.palette.primary.main, 0.5) },
    '@supports not selector(::-webkit-scrollbar)': { scrollbarWidth: 'thin', scrollbarColor: `${thumb} transparent` },
  };
};

/**
 * The groups above the graph list: "Alle", one chip per group, "+ Neue
 * Gruppe". The open group's chip turns into a name field while it is renamed.
 *   groups      [{id, name, count}]
 *   onCreate    (name) => Promise — rejects with GroupNameTaken
 *   onRename    (id, name) => Promise
 */
function GraphGroupsBar({ groups, total, activeId, onSelect, onCreate, renamingId, onRename, onRenameEnd }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [creating, setCreating] = useState(false);

  // A thin bar is hard to grab: the mouse wheel scrolls the row sideways too.
  const onWheel = (e) => {
    const row = e.currentTarget;
    if (row.scrollWidth > row.clientWidth && Math.abs(e.deltaY) > Math.abs(e.deltaX)) row.scrollLeft += e.deltaY;
  };

  return (
    <Box
      role="toolbar"
      aria-label={t('graphs_groups', { defaultValue: 'Gruppen' })}
      onWheel={onWheel}
      sx={{
        display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5, pb: 0.75,
        overflowX: 'auto', overflowY: 'hidden', flexShrink: 0,
        ...thinScrollbarSx(theme),
      }}
    >
      <Chip label={t('graphs_all', { defaultValue: 'Alle' })} count={total} active={activeId == null}
        onClick={() => onSelect(null)} />
      {groups.map(group => (group.id === renamingId ? (
        <GroupNameInput key={group.id} initial={group.name}
          onSubmit={async (name) => { await onRename(group.id, name); onRenameEnd(); }}
          onCancel={onRenameEnd} />
      ) : (
        <Chip key={group.id} label={group.name} count={group.count} active={group.id === activeId}
          onClick={() => onSelect(group.id)} />
      )))}
      {creating ? (
        <GroupNameInput
          onSubmit={async (name) => { await onCreate(name); setCreating(false); }}
          onCancel={() => setCreating(false)} />
      ) : (
        <ButtonBase
          onClick={() => setCreating(true)}
          sx={{
            ...pillSx(theme), borderStyle: 'dashed', bgcolor: 'transparent', gap: 0.5, pl: 1,
            '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
          }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
          {t('graphs_group_new', { defaultValue: 'Neue Gruppe' })}
        </ButtonBase>
      )}
    </Box>
  );
}

export default GraphGroupsBar;
